import React, { useState, useEffect, useMemo } from 'react';
import { 
  Mail, Star, Trash2, Eye, EyeOff, ArrowLeft, RefreshCw, 
  Inbox, Clock, Send, AlertCircle, Lock,
  CheckSquare, Square, XCircle, Settings, KeyRound, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  
  // Password change state
  const [showSettings, setShowSettings] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [stats, setStats] = useState({ total: 0, unread: 0, starred: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  // Check if already authenticated (session storage)
  useEffect(() => {
    const authSession = sessionStorage.getItem('admin_auth');
    if (authSession === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_auth', 'true');
        setAuthError('');
      } else {
        setAuthError('Incorrect password');
        setPassword('');
      }
    } catch (err) {
      setAuthError('Failed to verify password. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setShowSettings(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');
    
    // Validation
    if (newPassword.length < 6) {
      setChangePasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setChangePasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Auto-close settings after 2 seconds
        setTimeout(() => {
          setShowSettings(false);
          setChangePasswordSuccess('');
        }, 2000);
      } else {
        setChangePasswordError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setChangePasswordError('Failed to change password. Please try again.');
      console.error('Password change error:', err);
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/contact/submissions`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setSubmissions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load submissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact/stats`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
      fetchStats();
    }
  }, [isAuthenticated]);

  // Filtered submissions
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(s => {
      if (filter === 'unread') return !s.is_read;
      if (filter === 'starred') return s.is_starred;
      return true;
    });
  }, [submissions, filter]);

  // Selection functions
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const filteredIds = filteredSubmissions.map(s => s.id);
    setSelectedIds(new Set(filteredIds));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const isAllSelected = filteredSubmissions.length > 0 && 
    filteredSubmissions.every(s => selectedIds.has(s.id));

  // Bulk actions
  const bulkMarkAsRead = async (isRead) => {
    try {
      const promises = Array.from(selectedIds).map(id =>
        fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_read: isRead })
        })
      );
      await Promise.all(promises);
      setSubmissions(submissions.map(s => 
        selectedIds.has(s.id) ? { ...s, is_read: isRead } : s
      ));
      fetchStats();
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk update failed:', err);
    }
  };

  const bulkToggleStar = async (isStarred) => {
    try {
      const promises = Array.from(selectedIds).map(id =>
        fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_starred: isStarred })
        })
      );
      await Promise.all(promises);
      setSubmissions(submissions.map(s => 
        selectedIds.has(s.id) ? { ...s, is_starred: isStarred } : s
      ));
      fetchStats();
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk star failed:', err);
    }
  };

  const bulkDelete = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} submission${count > 1 ? 's' : ''}?`)) return;
    
    try {
      const promises = Array.from(selectedIds).map(id =>
        fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
          method: 'DELETE'
        })
      );
      await Promise.all(promises);
      setSubmissions(submissions.filter(s => !selectedIds.has(s.id)));
      if (selectedSubmission && selectedIds.has(selectedSubmission.id)) {
        setSelectedSubmission(null);
      }
      fetchStats();
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk delete failed:', err);
    }
  };

  // Single item actions
  const markAsRead = async (id, isRead) => {
    try {
      await fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_read: isRead })
      });
      setSubmissions(submissions.map(s => 
        s.id === id ? { ...s, is_read: isRead } : s
      ));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, is_read: isRead });
      }
      fetchStats();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const toggleStar = async (id, isStarred) => {
    try {
      await fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_starred: isStarred })
      });
      setSubmissions(submissions.map(s => 
        s.id === id ? { ...s, is_starred: isStarred } : s
      ));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, is_starred: isStarred });
      }
      fetchStats();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/contact/submissions/${id}`, {
        method: 'DELETE'
      });
      setSubmissions(submissions.filter(s => s.id !== id));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(null);
      }
      fetchStats();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const selectSubmission = (submission) => {
    setSelectedSubmission(submission);
    if (!submission.is_read) {
      markAsRead(submission.id, true);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Password Protection Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#080808] rounded-2xl border border-white/10 p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-14 h-14 rounded-xl bg-[#c8ff00] flex items-center justify-center">
                <Lock className="w-7 h-7 text-black" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Access</h1>
            <p className="text-gray-500 text-center mb-8">Enter password to continue</p>

            <form onSubmit={handleLogin} data-testid="admin-login-form">
              {authError && (
                <div className="mb-4 p-3 rounded-lg bg-[#ff6b6b]/10 border border-[#ff6b6b]/30 flex items-center gap-2" data-testid="admin-login-error">
                  <XCircle className="w-4 h-4 text-[#ff6b6b]" />
                  <span className="text-[#ff6b6b] text-sm">{authError}</span>
                </div>
              )}
              
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0c0c0c] border-white/10 text-white placeholder:text-gray-600 rounded-xl h-12 mb-4 focus:border-[#c8ff00]"
                autoFocus
                disabled={authLoading}
                data-testid="admin-password-input"
              />
              
              <Button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[#c8ff00] hover:bg-[#b8ef00] text-black font-semibold rounded-xl h-12 disabled:opacity-50"
                data-testid="admin-login-submit"
              >
                {authLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock Dashboard
                  </>
                )}
              </Button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="w-full mt-6 text-gray-500 hover:text-white text-sm transition-colors"
            >
              ← Back to website
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Settings Modal
  if (showSettings) {
    return (
      <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#080808] rounded-2xl border border-white/10 p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#c8ff00]/20 flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-[#c8ff00]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Change Password</h1>
                  <p className="text-sm text-gray-500">Update your admin password</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSettings(false);
                  setChangePasswordError('');
                  setChangePasswordSuccess('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleChangePassword}>
              {changePasswordError && (
                <div className="mb-4 p-3 rounded-lg bg-[#ff6b6b]/10 border border-[#ff6b6b]/30 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-[#ff6b6b]" />
                  <span className="text-[#ff6b6b] text-sm">{changePasswordError}</span>
                </div>
              )}
              
              {changePasswordSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-[#4ade80]/10 border border-[#4ade80]/30 flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#4ade80]" />
                  <span className="text-[#4ade80] text-sm">{changePasswordSuccess}</span>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Current Password</label>
                  <Input
                    type="password"
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-[#0c0c0c] border-white/10 text-white placeholder:text-gray-600 rounded-xl h-12 focus:border-[#c8ff00]"
                    required
                    disabled={changingPassword}
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">New Password</label>
                  <Input
                    type="password"
                    placeholder="Enter new password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-[#0c0c0c] border-white/10 text-white placeholder:text-gray-600 rounded-xl h-12 focus:border-[#c8ff00]"
                    required
                    minLength={6}
                    disabled={changingPassword}
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Confirm New Password</label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[#0c0c0c] border-white/10 text-white placeholder:text-gray-600 rounded-xl h-12 focus:border-[#c8ff00]"
                    required
                    disabled={changingPassword}
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={changingPassword}
                className="w-full mt-6 bg-[#c8ff00] hover:bg-[#b8ef00] text-black font-semibold rounded-xl h-12 disabled:opacity-50"
              >
                {changingPassword ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Changing Password...
                  </span>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-[#0c0c0c]">
      {/* Header */}
      <header className="bg-[#080808] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#c8ff00] flex items-center justify-center">
                  <Send className="w-5 h-5 text-black -rotate-12" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                  <p className="text-sm text-gray-500">Contact Form Submissions</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => { fetchSubmissions(); fetchStats(); }}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-[#ff6b6b]/30 text-[#ff6b6b] hover:bg-[#ff6b6b]/10"
              >
                <Lock className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 md:gap-8">
              <button
                onClick={() => setFilter('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  filter === 'all' ? 'bg-[#c8ff00]/10 text-[#c8ff00]' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Inbox className="w-4 h-4" />
                <span>All</span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs">{stats.total}</span>
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  filter === 'unread' ? 'bg-[#c8ff00]/10 text-[#c8ff00]' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>Unread</span>
                <span className="px-2 py-0.5 rounded-full bg-[#ff6b6b]/20 text-[#ff6b6b] text-xs">{stats.unread}</span>
              </button>
              <button
                onClick={() => setFilter('starred')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  filter === 'starred' ? 'bg-[#c8ff00]/10 text-[#c8ff00]' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Star className="w-4 h-4" />
                <span>Starred</span>
                <span className="px-2 py-0.5 rounded-full bg-[#fbbf24]/20 text-[#fbbf24] text-xs">{stats.starred}</span>
              </button>
            </div>

            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#c8ff00]/10 border border-[#c8ff00]/30">
                <span className="text-[#c8ff00] text-sm font-medium">{selectedIds.size} selected</span>
                <div className="w-px h-4 bg-white/20 mx-2" />
                <button
                  onClick={() => bulkMarkAsRead(true)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Mark as read"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={() => bulkMarkAsRead(false)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Mark as unread"
                >
                  <EyeOff className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={() => bulkToggleStar(true)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Star selected"
                >
                  <Star className="w-4 h-4 text-[#fbbf24]" />
                </button>
                <button
                  onClick={bulkDelete}
                  className="p-1.5 hover:bg-[#ff6b6b]/20 rounded-lg transition-colors"
                  title="Delete selected"
                  data-testid="bulk-delete-button"
                >
                  <Trash2 className="w-4 h-4 text-[#ff6b6b]" />
                </button>
                <button
                  onClick={deselectAll}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Clear selection"
                >
                  <XCircle className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-[#ff6b6b]/10 border border-[#ff6b6b]/30 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#ff6b6b]" />
            <p className="text-[#ff6b6b]">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#c8ff00]/30 border-t-[#c8ff00] rounded-full animate-spin" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-20">
            <Inbox className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No submissions yet</h3>
            <p className="text-gray-500">Contact form submissions will appear here</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[420px_1fr] gap-6">
            {/* Submissions List */}
            <div className="bg-[#080808] rounded-2xl border border-white/10 overflow-hidden">
              {/* Select All Header */}
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <button
                  onClick={isAllSelected ? deselectAll : selectAll}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {isAllSelected ? (
                    <CheckSquare className="w-4 h-4 text-[#c8ff00]" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  <span>{isAllSelected ? 'Deselect all' : 'Select all'}</span>
                </button>
                <span className="text-xs text-gray-500">{filteredSubmissions.length} messages</span>
              </div>

              <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
                {filteredSubmissions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No {filter} submissions
                  </div>
                ) : (
                  filteredSubmissions.map((submission) => (
                    <div
                      key={submission.id}
                      className={`flex items-start gap-3 p-4 border-b border-white/5 transition-all ${
                        selectedSubmission?.id === submission.id
                          ? 'bg-[#c8ff00]/10 border-l-2 border-l-[#c8ff00]'
                          : 'hover:bg-white/5 border-l-2 border-l-transparent'
                      }`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelect(submission.id);
                        }}
                        className="mt-1 flex-shrink-0"
                        data-testid={`submission-checkbox-${submission.id}`}
                      >
                        {selectedIds.has(submission.id) ? (
                          <CheckSquare className="w-5 h-5 text-[#c8ff00]" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-600 hover:text-gray-400" />
                        )}
                      </button>

                      {/* Content */}
                      <button
                        onClick={() => selectSubmission(submission)}
                        className="flex-1 text-left min-w-0"
                        data-testid={`submission-row-${submission.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            {!submission.is_read && (
                              <span className="w-2 h-2 rounded-full bg-[#c8ff00] flex-shrink-0" />
                            )}
                            <span className={`font-medium truncate ${
                              submission.is_read ? 'text-gray-400' : 'text-white'
                            }`}>
                              {submission.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {submission.is_starred && (
                              <Star className="w-4 h-4 text-[#fbbf24] fill-[#fbbf24]" />
                            )}
                            <span className="text-xs text-gray-500">
                              {formatDate(submission.created_at)}
                            </span>
                          </div>
                        </div>
                        <p className={`text-sm mb-1 truncate ${
                          submission.is_read ? 'text-gray-500' : 'text-white font-medium'
                        }`}>
                          {submission.subject}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {submission.message}
                        </p>
                        <div className="mt-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-gray-400">
                            {submission.service}
                          </span>
                        </div>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Submission Detail */}
            <div className="bg-[#080808] rounded-2xl border border-white/10 overflow-hidden">
              {selectedSubmission ? (
                <div className="h-full flex flex-col">
                  {/* Detail Header */}
                  <div className="p-6 border-b border-white/10">
                    <div className="flex items-start justify-between mb-4">
                      <h2 className="text-2xl font-bold text-white pr-4">{selectedSubmission.subject}</h2>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleStar(selectedSubmission.id, !selectedSubmission.is_starred)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Toggle star"
                          data-testid="detail-star-button"
                        >
                          <Star className={`w-5 h-5 ${
                            selectedSubmission.is_starred 
                              ? 'text-[#fbbf24] fill-[#fbbf24]' 
                              : 'text-gray-500'
                          }`} />
                        </button>
                        <button
                          onClick={() => markAsRead(selectedSubmission.id, !selectedSubmission.is_read)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Toggle read"
                          data-testid="detail-read-button"
                        >
                          {selectedSubmission.is_read ? (
                            <EyeOff className="w-5 h-5 text-gray-500" />
                          ) : (
                            <Eye className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteSubmission(selectedSubmission.id)}
                          className="p-2 hover:bg-[#ff6b6b]/20 rounded-lg transition-colors"
                          title="Delete submission"
                          data-testid="detail-delete-button"
                        >
                          <Trash2 className="w-5 h-5 text-[#ff6b6b]" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#c8ff00] to-[#a3cc00] flex items-center justify-center">
                        <span className="text-black font-bold text-lg">
                          {selectedSubmission.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{selectedSubmission.name}</p>
                        <a 
                          href={`mailto:${selectedSubmission.email}`}
                          className="text-sm text-[#c8ff00] hover:underline"
                        >
                          {selectedSubmission.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Detail Body */}
                  <div className="flex-1 p-6 overflow-y-auto">
                    <div className="flex items-center gap-4 mb-6 text-sm text-gray-500 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(selectedSubmission.created_at).toLocaleString()}</span>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-[#c8ff00]/10 text-[#c8ff00]">
                        {selectedSubmission.service}
                      </span>
                    </div>
                    <div className="prose prose-invert max-w-none">
                      <p className="text-gray-300 whitespace-pre-wrap leading-relaxed text-lg">
                        {selectedSubmission.message}
                      </p>
                    </div>
                  </div>

                  {/* Reply Action */}
                  <div className="p-6 border-t border-white/10">
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#c8ff00] text-black font-semibold hover:bg-[#b8ef00] transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      Reply via Email
                    </a>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center py-20">
                  <div className="text-center">
                    <Mail className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500">Select a submission to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
